import { useMemo } from 'react';
import { useSyncWatcher } from './useSyncConnector';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../stores/store';

/**
 * Hook to set up global synchronization for all relevant query keys
 * This should be used at the app root level to ensure it persists
 * across component mounts/unmounts
 *
 * @param disableAutoSync Optional flag to disable automatic syncing (default: false)
 */
export function useGlobalSync(disableAutoSync = false) {
  const { selectedProject, selectedEstimate, selectedChangeOrder } = useStore(useUnityBuildStore);

  const queryKeys = useMemo(
    () => [
      // Project related
      ['projects'],
      ['projectFolders'],

      // Estimate related
      ['estimates'],
      ...(selectedEstimate?.estimateid ? [['estimates', selectedEstimate.estimateid]] : []),
      ['estimateBreakouts'],
      ...(selectedEstimate?.estimateid ? [['estimateBreakouts', selectedEstimate.estimateid]] : []),

      // Assembly related
      ['countedAssemblies'],
      ...(selectedEstimate?.estimateid && selectedChangeOrder?.changeorderid
        ? [['countedAssemblies', selectedEstimate.estimateid, selectedChangeOrder.changeorderid]]
        : []),
      ['assembliesByFileId'],
      ['assemblyFolders'],

      // Change order related
      ['changeOrders'],
      ...(selectedEstimate?.estimateid ? [['changeOrders', selectedEstimate.estimateid]] : []),
      ['changeOrderBreakouts'],
      ...(selectedChangeOrder?.changeorderid
        ? [['changeOrderBreakouts', selectedChangeOrder.changeorderid]]
        : []),

      // Contract related
      ['contracts'],
      ...(selectedProject?.id ? [['contracts', selectedProject.id]] : []),

      // Closeout related
      ['directLabor'],
      ...(selectedEstimate?.estimateid ? [['directLabor', selectedEstimate.estimateid]] : []),
      ['laborFactoring'],
      ...(selectedEstimate?.estimateid ? [['laborFactoring', selectedEstimate.estimateid]] : []),
      ['incidentalLabor'],
      ...(selectedEstimate?.estimateid ? [['incidentalLabor', selectedEstimate.estimateid]] : []),
      ['indirectLabor'],
      ...(selectedEstimate?.estimateid ? [['indirectLabor', selectedEstimate.estimateid]] : []),
      ['equipment'],
      ...(selectedEstimate?.estimateid ? [['equipment', selectedEstimate.estimateid]] : []),
      ['generalExpense'],
      ...(selectedEstimate?.estimateid ? [['generalExpense', selectedEstimate.estimateid]] : []),
      ['subcontractExpense'],
      ...(selectedEstimate?.estimateid
        ? [['subcontractExpense', selectedEstimate.estimateid]]
        : []),
      ['quoteExpense'],
      ...(selectedEstimate?.estimateid ? [['quoteExpense', selectedEstimate.estimateid]] : []),

      // Default types
      ['defaultDirectLaborTypes'],
      ['defaultLaborFactoringTypes'],
      ['defaultIndirectLaborTypes'],
      ['defaultIncidentalLaborTypes'],
      ['defaultEquipmentExpenseTypes'],
      ['defaultGeneralExpenseTypes'],
      ['defaultSubcontractExpenseTypes'],
      ['defaultQuoteExpenseTypes'],

      // Other
      ['users'],
      ['roles'],
      ['uoms'],
      ['attributeGroups'],
      ['allowedVariables'],
      ['account'],
    ],
    [selectedProject?.id, selectedEstimate?.estimateid, selectedChangeOrder?.changeorderid]
  );

  // Set up the sync watcher at the app level
  useSyncWatcher(queryKeys, 1000, disableAutoSync);
}
